/**
 * ArchiveTaskCompletionToasts · 任务完成通知条（v2 · 单条聚合）
 *
 * 监听 redux task.pendingNotifications 队列 · 每出现一条 taskId 就触发一次右上
 * 角通知。但与 v1 相比，多条通知不再叠加成一摞（用户一次建夹 20 个会刷屏）：
 *
 *  · 若当前没有活跃的通知 → 弹单条 "「项目A」已完成分类，点击查看"
 *  · 若已有活跃通知（duration 还没走完） → 替换为聚合 "已完成 N 个文件夹的分类，
 *    点击查看"。同 key 复用 notification.open · antd 会原地更新内容并重置 8s 倒计时
 *  · 通知 onClose（duration 到 / 用户手动关 / onClick 后自动关）→ 清空 aggregator，
 *    下一条进入又从单条形态开始
 *
 * 文案统一（与项目内"X 个文件夹"系列一致）：
 *  · 单条："「{folderName}」已完成分类，点击查看"
 *  · 聚合："已完成 {N} 个文件夹的分类，点击查看"
 *
 * 设计要点（继承自 v1）：
 *  · 队列在 redux 里维护并持久化（taskCompleted reducer 入队，本组件消费出队）
 *  · 旧 v1 用 antd 内置堆叠，多条会成摞挤屏；v2 改为单条聚合 · 复用同一 key
 *  · 不渲染任何 DOM · 纯副作用组件
 */

import React, { useEffect, useRef } from 'react'
import { notification } from 'antd'
import { CheckCircleFilled } from '@ant-design/icons'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import {
  notificationShown,
  selectAllTasks,
  selectPendingTaskNotifications
} from '../../../../slices/aiClassify'

interface Props {
  /** 通知点击 · 触发外部打开任务抽屉；通知会自动 close */
  onOpenDrawer: () => void
}

/** 通知保留时长（秒）· 8s 自动消失 · hover 暂停倒计时（antd 默认行为）
 *  · 同 key 再次 notification.open 会重置该倒计时 */
const NOTIFICATION_DURATION = 8

/** 聚合通知的固定 key · 同一时刻只挂一个，复用以原地更新内容 */
const AGGREGATE_KEY = 'ai-classify-task-aggregate'

const ArchiveTaskCompletionToasts: React.FC<Props> = ({ onOpenDrawer }) => {
  const dispatch = useAppDispatch()
  const pending = useAppSelector(selectPendingTaskNotifications)
  const tasks = useAppSelector(selectAllTasks)

  /** 最新的 onOpenDrawer 回调 ref · 让 notification.onClick 不被旧闭包绑死 */
  const onOpenRef = useRef(onOpenDrawer)
  useEffect(() => {
    onOpenRef.current = onOpenDrawer
  }, [onOpenDrawer])

  /**
   * 当前活跃 toast 的 aggregator
   *  · null = 没有活跃 toast，下一条进来就以"单条"形态弹
   *  · 非 null = 通知还没消失，新到的合并到 folderNames，重弹聚合文案
   *  · onClose 时归零（无论是手动关 / 倒计时到 / 点击后自动关）
   */
  const aggregatorRef = useRef<{
    taskIds: string[]
    folderNames: string[]
  } | null>(null)

  useEffect(() => {
    if (pending.length === 0) return

    // 1. 收集本轮新到的 taskId / folderName · 全部立即出队
    const newTaskIds: string[] = []
    const newFolderNames: string[] = []
    pending.forEach((taskId) => {
      const task = tasks.find((t) => t.taskId === taskId)
      newTaskIds.push(taskId)
      newFolderNames.push(task?.folderName ?? '未知相册')
      dispatch(notificationShown({ taskId }))
    })

    // 2. 合并到 aggregator（首次进入则新建，已有则追加）
    const prev = aggregatorRef.current
    const taskIds = [...(prev?.taskIds ?? []), ...newTaskIds]
    const folderNames = [...(prev?.folderNames ?? []), ...newFolderNames]
    aggregatorRef.current = { taskIds, folderNames }

    // 3. 决定文案 · 单条用具体名，聚合用 N 计数
    const count = folderNames.length
    const description =
      count === 1
        ? `「${folderNames[0]}」已完成分类，点击查看`
        : `已完成 ${count} 个文件夹的分类，点击查看`

    // 4. 同 key 弹 / 替换 · antd 3 会原地更新内容并重置倒计时
    notification.open({
      key: AGGREGATE_KEY,
      message: '分类任务已完成',
      description,
      icon: <CheckCircleFilled style={{ color: '#52c41a' }} />,
      placement: 'topRight',
      duration: NOTIFICATION_DURATION,
      onClick: () => {
        onOpenRef.current()
        notification.destroy(AGGREGATE_KEY)
        // onClose 也会跟着触发，aggregator 会被清空，这里不重复清
      },
      onClose: () => {
        aggregatorRef.current = null
      },
      style: { cursor: 'pointer' }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending])

  return null
}

export default ArchiveTaskCompletionToasts
